import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import api from "../../globals/axios";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../components/Alert";

const AssignProgramDialog = ({
  open,
  onClose,
  programId,
  onAssign,
  assignedManagers = [],
  userRole,
}) => {
  const [members, setMembers] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();

  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [open]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      let endpoint;
      if (userRole === "POP_MANAGER") {
        endpoint = "/popmanager/get-area-managers-list/";
      } else if (userRole === "AREA_MANAGER") {
        endpoint = "/areamanager/vles-list/";
      } else if (userRole === "VLE") {
        endpoint = "/vle/farmers-list/";
      }

      const response = await api.get(endpoint);
      userRole === "VLE"
        ? setMembers(response.data.results)
        : setMembers(response.data);
    } catch (error) {
      console.error("Error loading members:", error);
      showAlert("error", `Failed to load ${userRole.toLowerCase()} list`);
    } finally {
      setLoading(false);
    }
  };

  const handleManagerChange = (event, newValue) => {
    setSelectedManagers(newValue.map((manager) => manager.id));
  };

  const handleAssign = async () => {
    if (selectedManagers.length === 0) {
      showAlert("warning", "Please select at least one area manager");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(
        "/programs/create-program-recommendations/",
        {
          program_id: programId,
          recommended_users: selectedManagers,
        }
      );

      if (response.status === 201 || response.status === 200) {
        showAlert("success", "Program assigned successfully");
        onAssign();
        onClose();
      }
    } catch (error) {
      showAlert("error", "Failed to assign program");
      console.error("Assignment error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ position: "relative", p: 2 }}>
          <Typography variant="h6">
            {userRole === "POP_MANAGER" && t("Recommend to Area Managers")}
            {userRole === "AREA_MANAGER" && t("Recommend to VLEs")}
            {userRole === "VLE" && t("Recommend to Farmers")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div style={{ marginTop: "20px" }}>
              <Autocomplete
                multiple
                options={members}
                getOptionLabel={(option) => option.full_name || option.username}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={handleManagerChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={
                      userRole === "POP_MANAGER"
                        ? t("Select Area Managers")
                        : userRole === "AREA_MANAGER"
                        ? t("Select VLEs")
                        : userRole === "VLE"
                        ? t("Select Farmers")
                        : t("Select Users")
                    }
                    placeholder={
                      userRole === "POP_MANAGER"
                        ? t("Search Area Managers...")
                        : userRole === "AREA_MANAGER"
                        ? t("Search VLEs...")
                        : userRole === "VLE"
                        ? t("Search Farmers...")
                        : t("Search Members...")
                    }
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div
                      style={{
                        marginRight: "8px",
                        visibility: assignedManagers.includes(option.id)
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      <CheckIcon
                        style={{ color: "#4CAF50", fontSize: "20px" }}
                      />
                    </div>
                    {option.full_name || option.username}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.id}
                      label={option.full_name || option.username}
                      {...getTagProps({ index })}
                      disabled={assignedManagers.includes(option.id)}
                    />
                  ))
                }
                getOptionDisabled={(option) =>
                  assignedManagers.includes(option.id)
                }
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading} variant="outlined">
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleAssign}
            disabled={loading || selectedManagers.length === 0}
            variant="contained"
            color="primary"
          >
            {t("Assign")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignProgramDialog;
